import { useEffect, useState } from "react";

import { territoryUrl } from "./terrainBase";

/**
 * ACCES PARTAGE AUX JEUX DE DONNEES DU TERRITOIRE.
 *
 * POURQUOI CE MODULE EXISTE. Ces fichiers etaient charges a l'interieur de
 * TerrainViewer, dans l'effet qui construit la scene three.js, et seulement si
 * la couche correspondante etait disponible. Deux consequences des qu'autre
 * chose que la carte veut lire la meme donnee — les graphiques d'etape, par
 * exemple :
 *
 *  1. un graphique de debit ne pouvait pas exister sans que la couche
 *     "discharge" soit activee sur l'etape, ce qui n'a aucun sens : montrer une
 *     courbe et colorier des stations sont deux gestes independants ;
 *  2. il aurait fallu retelecharger le fichier — withdrawals.json et
 *     communes.json font 1 Mo chacun — alors que le viewer l'a deja en memoire.
 *
 * Le cache est une Map de PROMESSES, pas de resultats : deux consommateurs qui
 * demandent le meme fichier dans le meme tick partagent une requete unique, au
 * lieu d'en lancer deux avant que la premiere n'ait repondu. Un echec est
 * retire du cache pour qu'un remontage puisse retenter.
 *
 * NE PASSENT PAS PAR ICI : heightmap.bin, rivers.json et les rasters .bin. Ils
 * n'ont qu'un seul consommateur (la scene) et rivers.json pese 4,7 Mo — en
 * garder la version analysee en memoire pour toute la seance couterait cher
 * sans rendre service a personne.
 */

// Les fichiers lisibles par plusieurs consommateurs. Un nom court par fichier :
// c'est lui qu'une specification de graphique declare (cf. chartSpecs.js).
export const SHARED_DATASETS = {
  withdrawals: "withdrawals.json",
  communes: "communes.json",
  climate: "climate.json",
  discharge: "discharge.json",
};

const cache = new Map();

function cacheKey(territoryId, name) {
  return `${territoryId}/${name}`;
}

/**
 * Charge un jeu de donnees partage. Rejette si le fichier est absent ou
 * illisible — l'appelant decide quoi en faire (la carte s'en passe, un
 * graphique le dit).
 */
export function loadDataset(territoryId, name) {
  const file = SHARED_DATASETS[name];
  if (!territoryId || !file) return Promise.reject(new Error(`jeu de donnees inconnu : ${name}`));

  const key = cacheKey(territoryId, name);
  if (cache.has(key)) return cache.get(key);

  const promise = fetch(`${territoryUrl(territoryId)}/${file}`)
    .then((response) => {
      if (!response.ok) throw new Error(`${file} introuvable (${response.status})`);
      return response.json();
    })
    .catch((error) => {
      cache.delete(key);
      throw error;
    });

  cache.set(key, promise);
  return promise;
}

/**
 * Version React. Rend `{ data, loading, error }` — les trois etats sont
 * distincts a l'affichage : un graphique qui charge et un graphique dont la
 * donnee manque ne se disent pas de la meme façon.
 *
 * `name` peut etre nul (un graphique pas encore configure ne demande rien).
 */
export function useDataset(territoryId, name) {
  const [state, setState] = useState({ key: null, data: null, loading: false, error: null });

  useEffect(() => {
    if (!territoryId || !name) {
      setState({ key: null, data: null, loading: false, error: null });
      return undefined;
    }
    const key = cacheKey(territoryId, name);
    let cancelled = false;
    loadDataset(territoryId, name)
      .then((data) => {
        if (!cancelled) setState({ key, data, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ key, data: null, loading: false, error });
      });
    return () => { cancelled = true; };
  }, [territoryId, name]);

  // LA DONNEE RENDUE DOIT REPONDRE A LA DEMANDE COURANTE, pas a la
  // precedente. Un effet ne s'execute qu'APRES le rendu : quand l'auteur
  // change le type d'un graphique dans le builder, le rendu qui suit demande
  // deja `withdrawals` alors que l'etat porte encore l'objet `communes`. Rendre
  // cet objet-la reviendrait a donner à une specification les donnees d'une
  // autre — un tableau attendu, un objet reçu, et l'ecran tombe. Tant que la
  // cle ne correspond pas, on est en chargement, ce que l'appelant sait
  // afficher.
  const key = territoryId && name ? cacheKey(territoryId, name) : null;
  if (state.key !== key) return { data: null, loading: Boolean(key), error: null };
  return { data: state.data, loading: state.loading, error: state.error };
}
